import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { confirmAction } from "@/lib/dialogs";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";


export default function Warehouses() {
  const { can } = usePermissions();
  const canCreateWarehouse = can("WAREHOUSES", "create");
  const canEditWarehouse = can("WAREHOUSES", "edit");
  const canDeleteWarehouse = can("WAREHOUSES", "delete");
  const [editingWh, setEditingWh] = useState(null);
  const removeWh = async (w) => {
    if (!(await confirmAction({ title: `Delete the warehouse ${w.name}?`, message: "This works only for a warehouse with no stock and no documents.", confirmLabel: "Delete" }))) return;
    try { await api.delete(`/inventory/warehouses/${w.id}`); toast.success(`${w.name} deleted`); setWarehouses((prev) => prev.filter((x) => x.id !== w.id)); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };
  const { companies } = useCompany() || {};
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/warehouses");
      setWarehouses(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || id;

  const shown = useMemo(() => {
    if (!search) return warehouses;
    const q = search.toLowerCase();
    return warehouses.filter((w) =>
      (w.code || "").toLowerCase().includes(q) ||
      (w.name || "").toLowerCase().includes(q) ||
      (w.city || "").toLowerCase().includes(q)
    );
  }, [warehouses, search]);

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      <PageHeader
        title="Warehouses"
        description="Site stores and depots. Stock is tracked per item, per warehouse."
        action={canCreateWarehouse && <CreateWarehouseDialog companies={companies} onCreated={load} />}
      />
      <PageStatCards loading={loading} stats={[
        { label: "Total Warehouses", value: warehouses.length, color: "blue" },
        { label: "Companies Covered", value: new Set(warehouses.map((w) => w.company_id)).size, color: "orange" },
        { label: "With Location", value: warehouses.filter((w) => w.city).length, color: "green" },
        { label: "Showing", value: shown.length, color: "gray" },
      ]} />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        csvData={shown.map((w) => ({ Code: w.code, Name: w.name, Company: companyName(w.company_id), City: w.city || "" }))}
        csvFilename="warehouses.csv"
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && warehouses.length === 0 ? (
          <EmptyState title="No warehouses yet" testId="warehouses-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((w) => (
                <TableRow key={w.id} data-testid={`warehouse-row-${w.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{w.code}</TableCell>
                  <TableCell className="text-foreground">{w.name}{w.is_active === false && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600" data-testid={`warehouse-inactive-${w.code}`}>Inactive</span>}</TableCell>
                  <TableCell className="text-muted-foreground">{companyName(w.company_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{w.city || "—"}</TableCell>
                  <TableCell>
                    <RowActions testId={`warehouse-actions-${w.code}`} onView={() => setSelected(w)} onEdit={canEditWarehouse ? () => setEditingWh(w) : undefined}
                      moreItems={canDeleteWarehouse ? [{ label: "Delete warehouse", icon: Trash2, danger: true, onClick: () => removeWh(w), testId: `warehouse-delete-${w.code}` }] : []} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={`Warehouse · ${selected?.code}`}
      >
        <DrawerSection label="Location Details">
          <DrawerField label="Code" value={selected?.code} mono />
          <DrawerField label="Name" value={selected?.name} />
          <DrawerField label="Company" value={selected ? companyName(selected.company_id) : null} />
          <DrawerField label="City / Location" value={selected?.city} />
        </DrawerSection>
      </DetailDrawer>
      {editingWh && <EditWarehouseDialog warehouse={editingWh} onClose={() => setEditingWh(null)} onSaved={(w) => { setEditingWh(null); setWarehouses((prev) => prev.map((x) => (x.id === w.id ? w : x))); }} />}
    </div>
  );
}

function CreateWarehouseDialog({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", code: "", name: "", city: "" });
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ company_id: "", code: "", name: "", city: "" }); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/warehouses", form);
      toast.success(`Warehouse ${form.code} created`);
      setForm({ company_id: "", code: "", name: "", city: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-warehouse-button"><Plus className="h-4 w-4" /> New Warehouse</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Warehouse</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-warehouse-form">
          <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="warehouse-company-select" />
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input placeholder="Automatic if blank" data-testid="warehouse-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /></div>
            <div><Label>Name</Label><Input required data-testid="warehouse-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
          </div>
          <div><Label>City</Label><Input data-testid="warehouse-city-input" value={form.city} onChange={set("city")} className="mt-1.5" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-warehouse-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/** Edit a warehouse: name, city, address, and whether it is in use. Its code and company stay. */
function EditWarehouseDialog({ warehouse, onClose, onSaved }) {
  const [form, setForm] = useState({ name: warehouse.name || "", city: warehouse.city || "", address: warehouse.address || "", is_active: warehouse.is_active !== false });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/inventory/warehouses/${warehouse.id}`, { ...form, city: form.city || null, address: form.address || null });
      toast.success(`${data.name} saved`);
      onSaved(data);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-warehouse-dialog">
        <DialogHeader><DialogTitle>Edit warehouse {warehouse.code}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="edit-warehouse-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="edit-warehouse-active" /> Active (an inactive warehouse is not offered for new receipts and issues)</label>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.name.trim()} data-testid="edit-warehouse-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
